import { useEffect, useState } from 'react'
import { listRecentPulls, PackPull } from '../api/packs'
import { rarityColor } from './PackOpening'

/** Compact "5m" / "just now" age from an ISO timestamp. */
const ago = (iso: string): string => {
    const secs = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000)
    if (secs < 60) return 'now'
    const mins = Math.floor(secs / 60)
    if (mins < 60) return `${mins}m`
    const hours = Math.floor(mins / 60)
    if (hours < 24) return `${hours}h`
    return `${Math.floor(hours / 24)}d`
}

const FeedCard = ({ pull }: { pull: PackPull }) => {
    const color = rarityColor(pull.rarity)
    return (
        <div
            className='flex shrink-0 items-center gap-3 border border-white/10 bg-black/50 px-3 py-2'
            style={{ borderLeft: `2px solid ${color}` }}
            title={`${pull.name ?? 'Weapon'} · ${pull.rarity ?? ''} · from ${pull.pack_name}`}
        >
            {/* The skin that was actually pulled, the way the reference shows
                the card itself. The rarity glyph is only a fallback: a pull whose
                art never came back would otherwise leave a hole in the rail. */}
            <div
                className='grid h-9 w-9 shrink-0 place-items-center overflow-hidden border'
                style={{ borderColor: `${color}66`, background: `${color}14` }}
            >
                {pull.gif_uri ? (
                    <img
                        src={pull.gif_uri}
                        alt={pull.name ?? ''}
                        loading='lazy'
                        className='h-full w-full object-contain'
                    />
                ) : (
                    <svg viewBox='0 0 24 16' className='h-4 w-4' style={{ color }} fill='currentColor'>
                        <rect x='2' y='6' width='15' height='4' rx='1' />
                        <rect x='15' y='5' width='6' height='2' rx='1' />
                        <rect x='6' y='10' width='3' height='4' rx='1' opacity='0.7' />
                    </svg>
                )}
            </div>
            <div className='flex flex-col leading-tight'>
                <span
                    className='max-w-[9rem] truncate text-sm font-black uppercase tracking-wide'
                    style={{ color }}
                >
                    {pull.name ?? pull.pack_name}
                </span>
                <span className='max-w-[9rem] truncate text-2xs font-bold uppercase tracking-[0.12em] text-white/40'>
                    {pull.rarity ?? pull.weapon_type ?? ''}
                </span>
            </div>
            <span className='ml-1 shrink-0 text-2xs font-semibold uppercase tracking-[0.15em] text-white/30'>
                {ago(pull.kept_at)}
            </span>
        </div>
    )
}

/**
 * The live pulls ticker across the top of the page — what other players are
 * pulling right now. Polls the feed on an interval; renders nothing until it
 * has something, so it never shows an empty rail.
 */
export const LiveFeed = ({ refreshKey = 0, items }: { refreshKey?: number; items?: PackPull[] }) => {
    const [pulls, setPulls] = useState<PackPull[]>(items ?? [])

    useEffect(() => {
        // Preview/harness path: render the supplied pulls and never hit the API.
        if (items) {
            setPulls(items)
            return
        }
        let alive = true
        const load = () =>
            listRecentPulls()
                .then((items) => alive && setPulls(items))
                .catch(() => {})
        load()
        // A slow poll keeps the ticker fresh without hammering the DB; a keep
        // already forces an immediate reload via refreshKey, so 20s is plenty.
        const timer = setInterval(load, 20000)
        return () => {
            alive = false
            clearInterval(timer)
        }
        // refreshKey lets the page force an immediate reload after a keep.
    }, [refreshKey, items])

    if (pulls.length === 0) return null

    return (
        <div className='flex items-center gap-3'>
            <div className='flex shrink-0 items-center gap-2 pr-1'>
                <span className='h-2 w-2 animate-pulse rounded-full bg-emerald-400 shadow-[0_0_8px_#34d399]' />
                <span className='text-2xs font-black uppercase tracking-[0.2em] text-emerald-400'>Live</span>
            </div>
            {/* Horizontal scroller — the row of recent pulls. */}
            <div className='flex flex-1 gap-2 overflow-x-auto pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden'>
                {pulls.map((pull) => (
                    <FeedCard key={pull.session_uuid} pull={pull} />
                ))}
            </div>
        </div>
    )
}
